import React, { useState } from 'react';
import { Lock, AlertCircle, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { api } from '../../api/client';
import {
    generateSalt,
    deriveKeyFromPassword,
    encryptPrivateKey,
    getMyKeys,
} from '../../utils/crypto';
import './Auth.css';

interface ResetPasswordPageProps {
    token: string;
    onSuccess: () => void;
}

export default function ResetPasswordPage({ token, onSuccess }: ResetPasswordPageProps) {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
        if (!/[a-z]/.test(pwd)) return 'Password must contain a lowercase letter';
        if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validatePassword(password);
        if (validationError) {
            setError(validationError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);

        try {
            await api.resetPassword(token, password);

            // ── E2EE key re-encryption ──────────────────────────────────────────
            // If the user arrived here via OAuth recovery, their private key was
            // restored to localStorage. We must re-encrypt it with the NEW password
            // and upload the new blob so that future password-based logins work.
            try {
                // Look for keys in the legacy/intermediate slot (where OAuthRecoveryPage puts them)
                const keys = getMyKeys();
                if (keys && keys.privateKey && keys.publicKey) {
                    const newSalt = generateSalt();
                    const newWrappingKey = await deriveKeyFromPassword(password, newSalt);
                    const newEncPrivateKey = await encryptPrivateKey(keys.privateKey, newWrappingKey);
                    await api.saveKeys({
                        enc_private_key: newEncPrivateKey,
                        key_salt: newSalt,
                        public_key: keys.publicKey,
                    });
                    console.info('[E2EE] Private key re-encrypted with new password.');
                }
            } catch (keyErr) {
                console.warn('[E2EE] Key re-encryption after reset failed:', keyErr);
            }
            // ─────────────────────────────────────────────────────────────────────

            setIsSuccess(true);
            setTimeout(onSuccess, 2000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to reset password');
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-header">
                        <div className="auth-logo">TC</div>
                        <h1>Password Updated!</h1>
                        <p>Redirecting to login...</p>
                    </div>
                    <div className="auth-success">
                        <CheckCircle size={16} />
                        <span>Your password has been successfully reset.</span>
                    </div>
                </div>
            </div>
        );
    }

    const requirements = [
        { met: password.length >= 8, text: 'At least 8 characters' },
        { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
        { met: /[a-z]/.test(password), text: 'One lowercase letter' },
        { met: /[0-9]/.test(password), text: 'One number' },
    ];

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <div className="auth-logo">TC</div>
                    <h1>Reset Password</h1>
                    <p>Enter your new password</p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {/* E2EE Key Loss Warning */}
                    <div className="auth-warning">
                        <span className="auth-warning-icon">⚠️</span>
                        <div className="auth-warning-content">
                            <span className="auth-warning-title">Encrypted message history will be lost</span>
                            <p className="auth-warning-text">
                                Resetting your password creates a new encryption key. You will no longer be able to read your previous encrypted messages. Your administrator may be able to recover them on your behalf.
                            </p>
                        </div>
                    </div>

                    {error && (
                        <div className="auth-error">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="auth-field">
                        <label htmlFor="password">New Password</label>
                        <div className="auth-input-wrapper">
                            <Lock size={18} />
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                className="auth-toggle-password"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="password-requirements">
                        {requirements.map((req, i) => (
                            <div key={i} className={`requirement ${req.met ? 'met' : ''}`}>
                                <CheckCircle size={14} />
                                <span>{req.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className="auth-field">
                        <label htmlFor="confirm">Confirm Password</label>
                        <div className="auth-input-wrapper">
                            <Lock size={18} />
                            <input
                                id="confirm"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                disabled={isLoading}
                            />
                        </div>
                    </div>

                    <button type="submit" className="auth-submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="spin" />
                                Resetting...
                            </>
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
